/*
  mezuzi, theme 170.0 (#110) - the cart drawer's header + content fade in, as the source.

  The source fades `.drawer__header` and `.drawer__content` (opacity + visibility, 0.5s cubic-bezier(0.3, 1, 0.3, 1),
  0.35s late from 640) and moves keyboard focus into the drawer only when the fade is over - onto the header itself
  (review-170/k/out/src110f-*.json: focus stays on the cart icon until ~1 s, then the header; the next Tab = its first
  control). Ours: the fade is CSS keyed on `data-cart-shown` (base.css, #110), set here one frame after the host opens,
  so a re-render while open inserts content already shown (no @starting-style replay). The hidden state itself is armed
  by this file (data-cart-fade) - without it the cart is simply visible. Tinker's own focus() on the
  close button runs while the header is still hidden and lands on the dialog; when the header's fade ends, focus goes
  to the header (tabindex -1) - the next Tab reaches the close. Under reduced motion there is no fade: at once.
*/

const HOST = 'theme-drawer#cart-drawer';

/** @param {HTMLElement} host */
function watch(host) {
  const dialog = host.querySelector(':scope > dialog');
  if (!dialog) return;
  // Arms the hidden state (base.css keys it on this attribute): if this file never runs, the cart opens fully visible, no fade.
  dialog.dataset.cartFade = '';
  let token = 0;

  const show = () => {
    const mine = ++token;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (mine !== token || !host.hasAttribute('open')) return;
        dialog.dataset.cartShown = '';
        const header = /** @type {HTMLElement | null} */ (dialog.querySelector('.theme-drawer__header'));
        if (!header) return;
        const style = getComputedStyle(header);
        const duration = parseFloat(style.transitionDuration) || 0;
        const wait = duration > 0 ? duration + (parseFloat(style.transitionDelay) || 0) : 0; // reduced motion: no transition, a delay alone is not one
        const focusHeader = () => {
          if (mine !== token || !host.hasAttribute('open') || !dialog.open) return;
          if (dialog.contains(document.activeElement) && document.activeElement !== dialog) return;
          if (!header.hasAttribute('tabindex')) header.setAttribute('tabindex', '-1');
          header.focus({ preventScroll: true });
        };
        if (wait === 0) return focusHeader();
        const done = (/** @type {TransitionEvent} */ event) => {
          if (event.target !== header || event.propertyName !== 'opacity') return;
          header.removeEventListener('transitionend', done);
          focusHeader();
        };
        header.addEventListener('transitionend', done);
        setTimeout(() => {
          header.removeEventListener('transitionend', done);
          focusHeader();
        }, wait * 1000 + 100);
      })
    );
  };

  const hide = () => {
    token++;
    delete dialog.dataset.cartShown;
  };

  new MutationObserver(() => (host.hasAttribute('open') ? show() : hide())).observe(host, { attributes: true, attributeFilter: ['open'] });
  if (host.hasAttribute('open')) show();
}

const host = document.querySelector(HOST);
if (host instanceof HTMLElement) watch(host);
